import React, { useState, useEffect } from 'react';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import MLAnalyticsDashboard from '../components/analytics/MLAnalyticsDashboard';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

// ENHANCED: Export Button Component
const ExportButton = ({ data, protocol }) => {
  const exportToCSV = () => {
    if (!data || !data.tables) return;

    let csvContent = `ODAG Analytics Export - ${protocol}\n\n`;
    
    data.tables.forEach(table => {
      csvContent += `${table.universe}\n`;
      csvContent += `Scenario,Total,Timely,%,Monthly,Quarterly,Annual\n`;
      
      table.breakdown.forEach(scenario => {
        csvContent += `"${scenario.scenario}",${scenario.total},${scenario.timelyCount},${scenario.timelyPct}%,${scenario.monthlyTotal}/${scenario.monthlyTimely},${scenario.quarterlyTotal}/${scenario.quarterlyTimely},${scenario.yearlyTotal}/${scenario.yearlyTimely}\n`;
      });
      
      csvContent += `"TABLE TOTAL",${table.overall.total},${table.overall.timelyCount},${table.overall.timelyPct}%,${table.overall.monthlyTotal}/${table.overall.monthlyTimely},${table.overall.quarterlyTotal}/${table.overall.quarterlyTimely},${table.overall.yearlyTotal}/${table.overall.yearlyTimely}\n\n`;
    });

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `odag-analytics-${protocol}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <button
      onClick={exportToCSV}
      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
    >
      📊 Export CSV
    </button>
  );
};

// ENHANCED: TableCard Component with comprehensive metrics
const EnhancedTableCard = ({ table, protocolName, isExpanded, onToggleExpand }) => {
  const [viewMode, setViewMode] = useState('table');
  const [period, setPeriod] = useState('monthly');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  if (!table || !table.overall) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-lg font-semibold text-gray-800">{table?.universe || 'Unknown Table'}</h4>
          <div className="text-xl font-bold text-gray-400">N/A</div>
        </div>
        <div className="text-gray-500 text-center py-4">No data available</div>
      </div>
    );
  }

  // Filter data based on selected period and date range
  const periodData = table.byPeriod?.[period] || [];
  const filteredPeriodData = periodData.filter(item => {
    if (!dateRange.start && !dateRange.end) return true;
    const itemDate = new Date(item.period);
    const startDate = dateRange.start ? new Date(dateRange.start) : null;
    const endDate = dateRange.end ? new Date(dateRange.end) : null;
    
    if (startDate && itemDate < startDate) return false;
    if (endDate && itemDate > endDate) return false;
    return true;
  });

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 min-w-0 overflow-hidden">
      {/* Table Header with Enhanced Metrics */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="min-w-0">
          <h4 className="text-lg font-semibold text-gray-800 truncate">{table.universe}</h4>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600 mt-1">
            <span>Total: {table.overall.total}</span>
            <span>Timely: {table.overall.timelyCount}</span>
            <span>Monthly: {table.overall.monthlyTotal}</span>
            <span>Quarterly: {table.overall.quarterlyTotal}</span>
            <span>Yearly: {table.overall.yearlyTotal}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className={`text-2xl font-bold ${
            table.overall.timelyPct >= 90 ? 'text-green-500' :
            table.overall.timelyPct >= 75 ? 'text-yellow-500' : 'text-red-500'
          }`}>
            {table.overall.timelyPct}%
          </div>
          <button
            onClick={onToggleExpand}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
            title={isExpanded ? 'Collapse' : 'Expand'}
          >
            {isExpanded ? '▼' : '▶'}
          </button>
        </div>
      </div>

      {/* Enhanced Controls */}
      {isExpanded && (
        <>
          <div className="flex flex-col gap-3 mb-4 pb-3 border-b border-gray-200">
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => setViewMode('table')}
                className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                  viewMode === 'table' 
                    ? 'bg-blue-100 text-blue-700 border border-blue-200' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                📊 Table
              </button>
              <button
                onClick={() => setViewMode('chart')}
                className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                  viewMode === 'chart' 
                    ? 'bg-blue-100 text-blue-700 border border-blue-200' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                📈 Chart
              </button>
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="yearly">Yearly</option>
              </select>
              <div className="flex flex-wrap items-center gap-1">
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                  className="px-2 py-1 border border-gray-300 rounded-lg text-sm bg-white max-w-[140px]"
                  placeholder="Start Date"
                />
                <span className="text-gray-400">–</span>
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                  className="px-2 py-1 border border-gray-300 rounded-lg text-sm bg-white max-w-[140px]"
                  placeholder="End Date"
                />
                {(dateRange.start || dateRange.end) && (
                  <button
                    onClick={() => setDateRange({ start: '', end: '' })}
                    className="px-2 py-1 text-xs rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Enhanced Table View */}
          {viewMode === 'table' && (
            <div className="space-y-4 min-w-0">
              {/* Scenario Breakdown Table */}
              <div className="overflow-x-auto -mx-1" style={{ maxWidth: '100%' }}>
                <table className="w-full min-w-[640px] text-sm border border-gray-200 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 sm:px-4 py-2 sm:py-3 text-left font-medium text-gray-700 border-b">Scenario Type</th>
                      <th className="px-2 sm:px-4 py-2 sm:py-3 text-center font-medium text-gray-700 border-b">Total</th>
                      <th className="px-2 sm:px-4 py-2 sm:py-3 text-center font-medium text-gray-700 border-b">Timely</th>
                      <th className="px-2 sm:px-4 py-2 sm:py-3 text-center font-medium text-gray-700 border-b">%</th>
                      <th className="px-2 sm:px-4 py-2 sm:py-3 text-center font-medium text-gray-700 border-b whitespace-nowrap">Monthly</th>
                      <th className="px-2 sm:px-4 py-2 sm:py-3 text-center font-medium text-gray-700 border-b whitespace-nowrap">Quarterly</th>
                      <th className="px-2 sm:px-4 py-2 sm:py-3 text-center font-medium text-gray-700 border-b whitespace-nowrap">Annual</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {table.breakdown.map((scenario, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-2 sm:px-4 py-2 sm:py-3 text-gray-900 font-medium truncate" title={scenario.scenario}>{scenario.scenario}</td>
                        <td className="px-2 sm:px-4 py-2 sm:py-3 text-center text-gray-600">{scenario.total}</td>
                        <td className="px-2 sm:px-4 py-2 sm:py-3 text-center text-gray-600">{scenario.timelyCount}</td>
                        <td className="px-2 sm:px-4 py-2 sm:py-3 text-center">
                          <span className={`font-medium ${
                            scenario.timelyPct >= 90 ? 'text-green-600' :
                            scenario.timelyPct >= 75 ? 'text-yellow-600' : 'text-red-600'
                          }`}>
                            {scenario.timelyPct}%
                          </span>
                        </td>
                        <td className="px-2 sm:px-4 py-2 sm:py-3 text-center text-gray-600">{scenario.monthlyTotal}/{scenario.monthlyTimely}</td>
                        <td className="px-2 sm:px-4 py-2 sm:py-3 text-center text-gray-600">{scenario.quarterlyTotal}/{scenario.quarterlyTimely}</td>
                        <td className="px-2 sm:px-4 py-2 sm:py-3 text-center text-gray-600">{scenario.yearlyTotal}/{scenario.yearlyTimely}</td>
                      </tr>
                    ))}
                    <tr className="bg-gray-50 font-semibold">
                      <td className="px-2 sm:px-4 py-2 sm:py-3 text-gray-900">TABLE TOTAL</td>
                      <td className="px-2 sm:px-4 py-2 sm:py-3 text-center text-gray-900">{table.overall.total}</td>
                      <td className="px-2 sm:px-4 py-2 sm:py-3 text-center text-gray-900">{table.overall.timelyCount}</td>
                      <td className="px-2 sm:px-4 py-2 sm:py-3 text-center">
                        <span className={`font-bold ${
                          table.overall.timelyPct >= 90 ? 'text-green-600' :
                          table.overall.timelyPct >= 75 ? 'text-yellow-600' : 'text-red-600'
                        }`}>
                          {table.overall.timelyPct}%
                        </span>
                      </td>
                      <td className="px-2 sm:px-4 py-2 sm:py-3 text-center text-gray-900">{table.overall.monthlyTotal}/{table.overall.monthlyTimely}</td>
                      <td className="px-2 sm:px-4 py-2 sm:py-3 text-center text-gray-900">{table.overall.quarterlyTotal}/{table.overall.quarterlyTimely}</td>
                      <td className="px-2 sm:px-4 py-2 sm:py-3 text-center text-gray-900">{table.overall.yearlyTotal}/{table.overall.yearlyTimely}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Period Breakdown */}
              {filteredPeriodData.length > 0 && (
                <div className="mt-6 min-w-0">
                  <h5 className="text-sm font-medium text-gray-700 mb-3">
                    {period.charAt(0).toUpperCase() + period.slice(1)} Performance Trend
                  </h5>
                  <div className="overflow-x-auto -mx-1" style={{ maxWidth: '100%' }}>
                    <table className="w-full min-w-[400px] text-sm border border-gray-200 rounded-lg">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left font-medium text-gray-700 border-b">Period</th>
                          <th className="px-4 py-3 text-center font-medium text-gray-700 border-b">Timeliness</th>
                          <th className="px-4 py-3 text-center font-medium text-gray-700 border-b">Cases</th>
                          <th className="px-4 py-3 text-center font-medium text-gray-700 border-b">Components</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {filteredPeriodData.map((item, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-gray-900">{item.period}</td>
                            <td className="px-4 py-3 text-center">
                              <span className={`font-medium ${
                                item.timelyPct >= 90 ? 'text-green-600' :
                                item.timelyPct >= 75 ? 'text-yellow-600' : 'text-red-600'
                              }`}>
                                {item.timelyPct}%
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center text-gray-600">
                              {item.timelyCount}/{item.total}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <div className="flex flex-wrap gap-1">
                                {item.components.slice(0, 3).map((comp, compIdx) => (
                                  <span key={compIdx} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                                    {comp.scenario}: {comp.timelyPct}%
                                  </span>
                                ))}
                                {item.components.length > 3 && (
                                  <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                                    +{item.components.length - 3} more
                                  </span>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Chart View (existing chart logic) */}
          {viewMode === 'chart' && (
            <div className="h-64">
              {filteredPeriodData.length > 0 ? (
                <Line 
                  data={{
                    labels: filteredPeriodData.map(p => p.period) || [],
                    datasets: [{
                      label: 'Timeliness %',
                      data: filteredPeriodData.map(p => p.timelyPct) || [],
                      borderColor: table.overall.timelyPct >= 90 ? 'rgb(34, 197, 94)' : 
                                   table.overall.timelyPct >= 75 ? 'rgb(251, 191, 36)' : 'rgb(239, 68, 68)',
                      backgroundColor: table.overall.timelyPct >= 90 ? 'rgba(34, 197, 94, 0.1)' : 
                                       table.overall.timelyPct >= 75 ? 'rgba(251, 191, 36, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                      borderWidth: 2,
                      tension: 0.4,
                      fill: true
                    }]
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { 
                      legend: { display: false },
                      tooltip: {
                        callbacks: {
                          label: (context) => `${context.parsed.y}% timely`
                        }
                      }
                    },
                    scales: {
                      x: { 
                        ticks: { color: '#6b7280', font: { size: 10 } },
                        grid: { display: false }
                      },
                      y: { 
                        ticks: { color: '#6b7280', font: { size: 10 } },
                        grid: { color: '#e5e7eb' },
                        beginAtZero: true,
                        max: 100
                      }
                    }
                  }}
                />
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  No {period} data available
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ENHANCED: Protocol Section Component
const ProtocolSection = ({ protocol, data, title }) => {
  const [expandedTables, setExpandedTables] = useState({});
  const [selectedPeriod, setSelectedPeriod] = useState('monthly');

  const toggleTableExpansion = (tableName) => {
    setExpandedTables(prev => ({
      ...prev,
      [tableName]: !prev[tableName]
    }));
  };

  if (!data) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-gray-800">{title}</h3>
          <div className="text-2xl font-bold text-gray-400">N/A</div>
        </div>
        <div className="text-gray-500 text-center py-8">No data available</div>
      </div>
    );
  }

  let tables = [];
  let overallStats = null;

  if (data.tables && Array.isArray(data.tables)) {
    tables = data.tables;
    overallStats = data.overall;
  } else if (data.overall) {
    tables = [data];
    overallStats = data.overall;
  }

  return (
    <div className="space-y-6 min-w-0">
      {/* Enhanced Protocol Header */}
      <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border border-gray-200 min-w-0 overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="min-w-0">
            <h3 className="text-xl sm:text-2xl font-semibold text-gray-800 truncate">{title}</h3>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600 mt-2">
              <span>{tables.length} table{tables.length !== 1 ? 's' : ''}</span>
              <span>Total Cases: {overallStats?.total || 0}</span>
              <span>Timely Cases: {overallStats?.timelyCount || 0}</span>
              <span>Monthly: {overallStats?.monthlyTotal || 0}</span>
              <span>Quarterly: {overallStats?.quarterlyTotal || 0}</span>
              <span>Yearly: {overallStats?.yearlyTotal || 0}</span>
            </div>
          </div>
          {overallStats && (
            <div className="text-left sm:text-right flex-shrink-0">
              <div className={`text-2xl sm:text-3xl font-bold ${
                overallStats.timelyPct >= 90 ? 'text-green-500' :
                overallStats.timelyPct >= 75 ? 'text-yellow-500' : 'text-red-500'
              }`}>
                {overallStats.timelyPct}%
              </div>
              <div className="text-sm text-gray-600 mt-1">Overall Timeliness</div>
              <div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-x-2 gap-y-0">
                <span>Monthly: {overallStats.monthlyPct || 0}%</span>
                <span>Quarterly: {overallStats.quarterlyPct || 0}%</span>
                <span>Yearly: {overallStats.yearlyPct || 0}%</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Enhanced Tables Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6 min-w-0">
        {tables.map((table, index) => (
          <EnhancedTableCard
            key={table.universe || index}
            table={table}
            protocolName={title}
            isExpanded={expandedTables[table.universe || index]}
            onToggleExpand={() => toggleTableExpansion(table.universe || index)}
          />
        ))}
      </div>

      {tables.length === 0 && (
        <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200 text-center">
          <div className="text-gray-500">No tables available for this protocol</div>
        </div>
      )}
    </div>
  );
};

// Simple placeholder for non-audit modules (MEMO, CAP, RISK)
const ModulePlaceholder = ({ title }) => {
  return (
    <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200">
      <h3 className="text-xl font-semibold text-gray-800 mb-2">{title}</h3>
      <p className="text-gray-500">Analytics for this module will appear here.</p>
    </div>
  );
};

// ENHANCED: Main Analytics Component
function Analytics({ user }) {
  const [protocolData, setProtocolData] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState({ type: 'protocol', code: 'ODAG' });
  // ENHANCED: Add new state variables for enhanced functionality
  const [selectedPeriod, setSelectedPeriod] = useState('monthly'); // monthly, quarterly, yearly
  const [expandedTables, setExpandedTables] = useState({});
  const [viewMode, setViewMode] = useState('table'); // table, chart
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  const apiBaseUrl = process.env.NODE_ENV === 'production' 
    ? 'https://cir-patrol-sea-greater.trycloudflare.com'  // Direct tunnel connection
    : 'http://localhost:5000';  // Direct connection in development

  console.log('🔧 Analytics API Base URL:', apiBaseUrl);
  console.log('🔧 NODE_ENV:', process.env.NODE_ENV);

  // Protocol configurations
  const protocols = [
    { code: 'ODAG', name: 'ODAG', title: 'Organization Determination Appeals', icon: '📁' },
    { code: 'CDAG', name: 'CDAG', title: 'Coverage Determination Appeals', icon: '🧾' },
    { code: 'CPE', name: 'CPE', title: 'Coverage Position Exceptions', icon: '🧩' },
    { code: 'SNPCC', name: 'SNPCC', title: 'Special Needs Plan Care Coordination', icon: '🩺' },
    { code: 'FA', name: 'FA', title: 'Formulary Appeals', icon: '💊' }
  ];

  const modules = [
    { code: 'MEMO', name: 'MEMO', icon: '📝' },
    { code: 'CAP', name: 'CAP', icon: '🎯' },
    { code: 'RISK', name: 'RISK', icon: '⚠️' },
    { code: 'ML', name: 'ML Analytics', icon: '🤖' },
  ];

  // Load protocol data
  useEffect(() => {
    if (!user) {
      console.log('No user, skipping load');
      setLoading(false);
      return;
    }

    console.log('Loading enhanced analytics data...');
    setLoading(true);
    
    // Set a timeout to prevent infinite loading
    const timeout = setTimeout(() => {
      console.log('Loading timeout - API calls failed');
      setLoading(false);
    }, 10000);
    
    Promise.all(
      protocols.map(protocol => {
        console.log(`🔄 Fetching enhanced ${protocol.code} from: ${apiBaseUrl}/api/analytics/${protocol.code}`);
        return fetch(`${apiBaseUrl}/api/analytics/${protocol.code}`, {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        })
          .then(res => {
            console.log(`${protocol.code} response status:`, res.status);
            if (!res.ok) {
              throw new Error(`HTTP ${res.status}`);
            }
            return res.json();
          })
          .then(body => {
            // API shape: { success, data }
            console.log(`${protocol.code} enhanced response:`, body);
            const payload = body?.data || body;
            console.log(`${protocol.code} enhanced payload:`, payload);
            console.log(`${protocol.code} payload type:`, typeof payload);
            console.log(`${protocol.code} is array:`, Array.isArray(payload));
            
            // Handle enhanced response structures
            if (payload?.tables && Array.isArray(payload.tables)) {
              // Enhanced ODAG with tables array and comprehensive metrics
              console.log(`${protocol.code} enhanced data with ${payload.tables.length} tables`);
              console.log(`${protocol.code} overall stats:`, payload.overall);
              return { 
                protocol: protocol.code, 
                data: payload
              };
            } else if (Array.isArray(payload)) {
              // Legacy ODAG returns raw array of tables - convert to enhanced format
              console.log(`${protocol.code} is legacy array with ${payload.length} tables`);
              
              // Calculate overall stats from individual tables
              const totalCases = payload.reduce((sum, table) => sum + (table.overall?.total || 0), 0);
              const totalTimely = payload.reduce((sum, table) => sum + (table.overall?.timelyCount || 0), 0);
              const overallTimelyPct = totalCases > 0 ? Math.round((totalTimely / totalCases) * 100) : 0;
              
              console.log(`${protocol.code} calculated legacy stats:`, { totalCases, totalTimely, overallTimelyPct });
              
              return { 
                protocol: protocol.code, 
                data: { 
                  overall: { 
                    timelyPct: overallTimelyPct, 
                    total: totalCases, 
                    timelyCount: totalTimely,
                    // Add default values for enhanced fields
                    monthlyTotal: 0,
                    quarterlyTotal: 0,
                    yearlyTotal: 0,
                    monthlyTimely: 0,
                    quarterlyTimely: 0,
                    yearlyTimely: 0,
                    monthlyPct: 0,
                    quarterlyPct: 0,
                    yearlyPct: 0
                  },
                  tables: payload 
                } 
              };
            } else if (payload?.overall) {
              // Single table data - wrap in enhanced format
              console.log(`${protocol.code} single table data`);
              return { 
                protocol: protocol.code, 
                data: {
                  overall: {
                    ...payload.overall,
                    // Add default values for enhanced fields if missing
                    monthlyTotal: payload.overall.monthlyTotal || 0,
                    quarterlyTotal: payload.overall.quarterlyTotal || 0,
                    yearlyTotal: payload.overall.yearlyTotal || 0,
                    monthlyTimely: payload.overall.monthlyTimely || 0,
                    quarterlyTimely: payload.overall.quarterlyTimely || 0,
                    yearlyTimely: payload.overall.yearlyTimely || 0,
                    monthlyPct: payload.overall.monthlyPct || 0,
                    quarterlyPct: payload.overall.quarterlyPct || 0,
                    yearlyPct: payload.overall.yearlyPct || 0
                  },
                  tables: [payload]
                }
              };
            } else {
              console.log(`${protocol.code} no valid data structure`);
              return { protocol: protocol.code, data: null };
            }
          })
          .catch(err => {
            console.error(`❌ Failed to fetch enhanced ${protocol.code}:`, err.message);
            return { protocol: protocol.code, data: null };
          });
      })
    )
      .then(results => {
        clearTimeout(timeout);
        console.log('All enhanced results:', results);
        const protocolMap = {};
        results.forEach(result => {
          protocolMap[result.protocol] = result.data;
        });
        console.log('Enhanced protocol map:', protocolMap);
        console.log('ODAG enhanced data in protocol map:', protocolMap.ODAG);
        console.log('ODAG tables count:', protocolMap.ODAG?.tables?.length);
        setProtocolData(protocolMap);
        setLoading(false);
      })
      .catch(err => {
        clearTimeout(timeout);
        console.error('Promise.all failed:', err);
        setLoading(false);
      });

    return () => clearTimeout(timeout);
  }, [user, apiBaseUrl]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">📊</span>
          </div>
          <div className="text-gray-900 text-lg font-medium">Loading Enhanced Analytics...</div>
          <div className="mt-2 text-gray-500 text-sm">Gathering comprehensive compliance metrics</div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-gray-50 overflow-hidden">
      <div className="flex h-full">
        {/* Enhanced Sidebar */}
        <aside className="w-48 bg-gradient-to-b from-slate-900 via-slate-900/95 to-slate-900 text-slate-200 border-r border-slate-800 p-3 overflow-y-auto shadow-xl">
          <div className="mb-6">
            <div className="text-[10px] tracking-wider font-semibold text-slate-400 uppercase mb-3 pl-2">Audit Protocols</div>
            <nav className="space-y-1.5">
              {protocols.map(p => {
                const isActive = selectedItem.type === 'protocol' && selectedItem.code === p.code;
                const pct = protocolData[p.code]?.overall?.timelyPct;
                const badgeColor = pct >= 90 ? 'text-emerald-400 bg-emerald-500/10' : pct >= 75 ? 'text-amber-400 bg-amber-500/10' : 'text-orange-400 bg-orange-500/10';
                return (
                  <button
                    key={p.code}
                    onClick={() => setSelectedItem({ type: 'protocol', code: p.code })}
                    className={`w-full flex items-center justify-between px-2.5 py-2 rounded-xl text-sm transition-all border ${
                      isActive
                        ? 'bg-slate-800/60 border-slate-700 shadow-inner'
                        : 'bg-slate-900/20 hover:bg-slate-800/40 border-slate-800'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <span className="w-6 h-6 rounded-md bg-slate-800 flex items-center justify-center text-sm">
                        {p.icon}
                      </span>
                      <span className={`font-medium ${isActive ? 'text-slate-100' : 'text-slate-300'}`}>{p.name}</span>
                    </span>
                    <span className={`px-2 py-0.5 rounded-md text-xs ${pct ? badgeColor : 'text-slate-400 bg-slate-700/50'}`}>
                      {pct ? `${pct}%` : 'N/A'}
                    </span>
                  </button>
                );
              })}
            </nav>
          </div>

          <div>
            <div className="text-[10px] tracking-wider font-semibold text-slate-400 uppercase mb-3 pl-2">Other Apps</div>
            <nav className="space-y-1.5">
              {modules.map(m => {
                const isActive = selectedItem.type === 'module' && selectedItem.code === m.code;
                return (
                  <button
                    key={m.code}
                    onClick={() => setSelectedItem({ type: 'module', code: m.code })}
                    className={`w-full flex items-center justify-between px-2.5 py-2 rounded-xl text-sm transition-all border ${
                      isActive
                        ? 'bg-slate-800/60 border-slate-700 shadow-inner'
                        : 'bg-slate-900/20 hover:bg-slate-800/40 border-slate-800'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <span className="w-6 h-6 rounded-md bg-slate-800 flex items-center justify-center text-sm">
                        {m.icon}
                      </span>
                      <span className={`font-medium ${isActive ? 'text-slate-100' : 'text-slate-300'}`}>{m.name}</span>
                    </span>
                    <span className="px-2 py-0.5 rounded-md text-xs text-slate-400 bg-slate-700/50">—</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </aside>

        {/* Enhanced Content */}
        <main className="flex-1 min-w-0 p-4 sm:p-6 overflow-y-auto overflow-x-hidden">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6 min-w-0">
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">ODAG Analytics Dashboard</h1>
              <p className="text-gray-600 text-sm">Comprehensive compliance metrics across all protocols and tables</p>
            </div>
            {selectedItem.type === 'protocol' && protocolData[selectedItem.code] && (
              <div className="flex-shrink-0">
                <ExportButton data={protocolData[selectedItem.code]} protocol={selectedItem.code} />
              </div>
            )}
          </div>

          {selectedItem.type === 'protocol' ? (
            <ProtocolSection
              protocol={selectedItem.code}
              data={protocolData[selectedItem.code]}
              title={protocols.find(p => p.code === selectedItem.code)?.title || selectedItem.code}
            />
          ) : selectedItem.code === 'ML' ? (
            <MLAnalyticsDashboard />
          ) : (
            <ModulePlaceholder title={`${modules.find(m => m.code === selectedItem.code)?.name || selectedItem.code} Analytics`} />
          )}
        </main>
      </div>
    </div>
  );
}

export default Analytics;